import { TFunction } from 'i18next';
import { api } from 'common/frontend/api';
import { Callable } from 'common/frontend/callable';
import { getIsCnameValid, getIsARecordValid } from 'common/modules/customDomains';
import { isDevEnv } from 'common/utils/common';

export const getLocationUrlsByUrl = (locationUrlsById: { [locationId: string]: string }) => {
	return Object.entries(locationUrlsById).reduce((result, [locationId, url]) => {
		result[url] = locationId;
		return result;
	}, {} as { [locationUrl: string]: string });
};

export const getResevedUrlsForLocation = (
	locationUrlsById: { [locationId: string]: string },
	locationId: string,
) => {
	return Object.keys(locationUrlsById)
		.filter((id) => id !== locationId)
		.map((id) => locationUrlsById[id]);
};

export const validateBaseURL = async (newURL: string, oldURL: string, t: TFunction) => {
	if (newURL.length < 3) {
		return t(
			'common:validation.urlMustBeAtLeast3Characters',
			'The URL must be at least 3 characters',
		);
	}

	const regex = new RegExp('^[a-z0-9-]{1,}$');
	if (!regex.test(newURL)) {
		return t(
			'common:validation.urlCanContainOnly',
			'The URL can only contain a-z, numbers and dashes',
		);
	}

	if (newURL !== oldURL) {
		const existingURL = await api().shopUrls.doc(newURL).get();
		if (!!existingURL) {
			return t('common:validation.urlAlreadyInUse', 'That URL is already in use');
		}
	}

	return true;
};

export const validateLocationURL = (newURL: string, reservedURLs: string[], t: TFunction) => {
	if (!newURL.length) {
		return t('validation.fieldCannotBeEmpty');
	}

	if (reservedURLs.includes(newURL)) {
		return t('common:validation.urlAlreadyInUse', 'That URL is already in use');
	}

	const regex = new RegExp('^[a-z0-9-]{1,}$');
	if (!regex.test(newURL)) {
		return t(
			'common:validation.urlCanContainOnly',
			'The URL can only contain a-z, numbers and dashes',
		);
	}

	return true;
};

export type RecordValidationResult = { valid: true } | { valid: false; error: string };

export const validateCnameRecord = async (
	cNameDomain: string,
	t: TFunction,
): Promise<RecordValidationResult> => {
	const dnsRecords = await Callable.customDomains.fetchDnsRecords({
		hostname: cNameDomain,
	});
	const cnameValues = dnsRecords
		.filter((record) => record.key === 'CNAME')
		.map((record) => record.value);
	if (!cnameValues.length) {
		return { valid: false, error: t('account.domains.noCnameRecord', 'No CNAME record found') };
	}
	const invalidCnames = cnameValues.filter(
		(cname) => !getIsCnameValid(cname, { isDevEnv: isDevEnv() }),
	);
	return !invalidCnames.length
		? { valid: true }
		: {
				valid: false,
				error: t('account.domains.invalidCname', {
					defaultValue: 'Incorrect CNAME value: {{value}}',
					value: invalidCnames[0],
				}),
		  };
};

export const validateARecords = async (
	aRecordsDomain: string,
	t: TFunction,
): Promise<RecordValidationResult> => {
	const dnsRecords = await Callable.customDomains.fetchDnsRecords({
		hostname: aRecordsDomain,
	});
	const aRecords = dnsRecords.filter((record) => record.key === 'A').map((record) => record.value);
	if (!aRecords.length) {
		return { valid: false, error: t('account.domains.noARecord', 'No A records found') };
	}
	const invalidARecords = aRecords.filter(
		(aRecord) => !getIsARecordValid(aRecord, { isDevEnv: isDevEnv() }),
	);
	return !invalidARecords.length
		? { valid: true }
		: {
				valid: false,
				error: t('account.domains.invalidARecords', {
					defaultValue: 'Incorrect A record values: {{value}}',
					value: invalidARecords.join(', '),
				}),
		  };
};
